/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, April 26th 2022, 1:01:37 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { RegistrationProfile, Gender, User } from "lib/schemas";
import { EditToggler } from "ui-widgets";
import { AutoSaveAPIForm, TextFormInput, DropDownFormInput, TextAreaFormInput, DateFormInput } from "ui-widgets/forms";
import { useRegistrationProfileAPIForm } from "users/services/UserAPI";

interface UserProfileImageFormProps {
  registrationProfile: RegistrationProfile;
  setUser: React.Dispatch<React.SetStateAction<User>>;
  editModeCondition: boolean;
}

export const UserPersonalInformation = ({
  registrationProfile,
  setUser,
  editModeCondition,
}: UserProfileImageFormProps) => {
  const useRegistrationProfileAPIFormReturn = useRegistrationProfileAPIForm(
    { ...registrationProfile, id: registrationProfile?.id },
    (profileData: RegistrationProfile) => setUser((prev) => ({ ...prev, registrationprofile: profileData }))
  );

  const {
    useFormReturn: {
      formState: { errors },
    },
    apiDataSendErrors,
  } = useRegistrationProfileAPIFormReturn;

  const fieldHasError = (fieldName: keyof RegistrationProfile) => fieldName in errors || fieldName in apiDataSendErrors;

  return (
    <AutoSaveAPIForm
      showSubmitButton={false}
      showFeedbackAlerts={false}
      useAPIFormReturn={useRegistrationProfileAPIFormReturn}
      sx={{}}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <div className="os-profile-field-label">Name</div>
        </Grid>
        <Grid item xs={12} md={6}>
          <EditToggler
            editModeCondition={editModeCondition}
            hasError={fieldHasError("firstname")}
            renderEditComponent={({ setValueInputRef }) => (
              <Stack direction="row" spacing={2}>
                <TextFormInput<RegistrationProfile>
                  schema_field_key="firstname"
                  label="First Name"
                  sx={{ width: "250px" }}
                  inputRef={setValueInputRef}
                />
                <TextFormInput<RegistrationProfile>
                  schema_field_key="surname"
                  label="Surname"
                  sx={{ width: "250px" }}
                />
              </Stack>
            )}
          >
            {({ editIcon }) => (
              <div className="os-profile-field-value">
                {registrationProfile?.firstname} {registrationProfile?.surname} {editIcon}
              </div>
            )}
          </EditToggler>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="os-profile-field-label">Date of Birth</div>
        </Grid>
        <Grid item xs={12} md={6}>
          <EditToggler
            editModeCondition={editModeCondition}
            hasError={fieldHasError("dob")}
            renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
              <DateFormInput<RegistrationProfile> inputRef={setValueInputRef} schema_field_key="dob" />
            )}
          >
            {({ editIcon }) => (
              <div className="os-profile-field-value">
                {registrationProfile?.dob ? new Date(registrationProfile?.dob).toDateString() : null} {editIcon}
              </div>
            )}
          </EditToggler>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="os-profile-field-label">National ID/Passport</div>
        </Grid>
        <Grid item xs={12} md={6}>
          <EditToggler
            editModeCondition={editModeCondition}
            hasError={fieldHasError("national_id")}
            renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
              <TextFormInput<RegistrationProfile> inputRef={setValueInputRef} schema_field_key="national_id" />
            )}
          >
            {({ editIcon }) => (
              <div className="os-profile-field-value">
                {registrationProfile?.national_id} {editIcon}
              </div>
            )}
          </EditToggler>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="os-profile-field-label">Gender</div>
        </Grid>
        <Grid item xs={12} md={6}>
          <EditToggler
            editModeCondition={editModeCondition}
            hasError={fieldHasError("gender")}
            renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
              <DropDownFormInput<RegistrationProfile>
                inputRef={setValueInputRef}
                schema_field_key="gender"
                options={Gender}
                sx={{ width: "250px" }}
              />
            )}
          >
            {({ editIcon }) => (
              <div className="os-profile-field-value">
                {Gender[registrationProfile?.gender]} {editIcon}
              </div>
            )}
          </EditToggler>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="os-profile-field-label">Phone</div>
        </Grid>
        <Grid item xs={12} md={6}>
          <EditToggler
            editModeCondition={editModeCondition}
            hasError={fieldHasError("phone_number")}
            renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
              <TextFormInput<RegistrationProfile> inputRef={setValueInputRef} schema_field_key="phone_number" />
            )}
          >
            {({ editIcon }) => (
              <div className="os-profile-field-value">
                {registrationProfile?.phone_number} {editIcon}
              </div>
            )}
          </EditToggler>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="os-profile-field-label">Biography</div>
        </Grid>
        <Grid item xs={12} md={6}>
          <EditToggler
            editModeCondition={editModeCondition}
            hasError={fieldHasError("biography")}
            renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
              <TextAreaFormInput<RegistrationProfile>
                sx={{ width: "100%" }}
                fullWidth
                inputRef={setValueInputRef}
                schema_field_key="biography"
              />
            )}
          >
            {({ editIcon }) => (
              <div className="os-profile-field-value">
                {registrationProfile?.biography} {editIcon}
              </div>
            )}
          </EditToggler>
        </Grid>
      </Grid>
    </AutoSaveAPIForm>
  );
};
